import {computed, Directive, effect, Input, OnChanges, signal, SimpleChanges} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {AbstractControl, NG_VALIDATORS, NgModel, ValidationErrors, Validator, ValidatorFn} from '@angular/forms';

/**
 * Custom validator function that checks if the control value equals another control's value
 */
export function equalToValidator(getOtherValue: () => any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const otherValue = getOtherValue();
    if (otherValue === undefined || otherValue === null) {
      return null;
    }
    const value = control.value;

    // If both are empty, don't show error (required validator will handle that)
    if (!value && !otherValue) {
      return null;
    }

    if (value === otherValue) {
      return null;
    }
    return {equalTo: true};
  };
}

/**
 * Directive that validates if the current field's value equals another field's value.
 * This is a replacement for ng2-validation's equalTo directive.
 * Uses Angular signals instead of RxJS subscriptions for better performance.
 *
 * Usage:
 * <input [(ngModel)]="confirmPassword" [equalTo]="newPassword" #confirmPassword="ngModel">
 */
@Directive({
  selector: '[equalTo]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: EqualToDirective,
      multi: true
    }
  ],
  standalone: true
})
export class EqualToDirective implements Validator, OnChanges {
  @Input() equalTo!: NgModel;

  private validatorFn: ValidatorFn = () => null;
  private control?: AbstractControl;
  // Signal that holds the signal from toSignal() - initialized with empty observable
  private valueChangesSignalRef = signal<ReturnType<typeof toSignal<any>> | null>(null);
  // Computed signal that reads the current value from the stored signal
  private otherControlValue = computed(() => {
    const signalRef = this.valueChangesSignalRef();
    return signalRef ? signalRef() : undefined;
  });

  constructor() {
    // Use effect to watch the other control's value and trigger re-validation
    // This effect runs in the constructor context, which is the correct place
    effect(() => {
      // Read the computed signal to establish the dependency
      this.otherControlValue();

      // Re-validate when the other control's value changes
      if (this.control) {
        this.control.updateValueAndValidity();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equalTo']) {
      // Convert the Observable to a signal if the control exists
      if (this.equalTo?.control) {
        const valueChangesSignal = toSignal(
          this.equalTo.control.valueChanges,
          {initialValue: this.equalTo.control.value}
        );
        // Store the signal reference - the effect in constructor will automatically watch it
        this.valueChangesSignalRef.set(valueChangesSignal);
      } else {
        this.valueChangesSignalRef.set(null);
      }

      // Create validator function that reads from the computed signal
      this.validatorFn = equalToValidator(() => this.otherControlValue());

      // Re-validate when the other control reference changes
      if (this.control) {
        this.control.updateValueAndValidity();
      }
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.control) {
      this.control = control;
    }
    return this.validatorFn(control);
  }
}

